import React from 'react';
import ReactMarkdown from 'react-markdown';
import ReHypeKatex from 'rehype-katex';
import ReHypeRaw from 'rehype-raw';
import RemarkMath from 'remark-math';
import RemarkGfm from 'remark-gfm';
import CodeBlock from './CodeBlock';

interface Props {
  text: string;
}

const FormattedMessage = ({ text }: Props) => {
  return (
    <ReactMarkdown
      remarkPlugins={[RemarkMath, RemarkGfm]}
      rehypePlugins={[
        ReHypeRaw,
        [
          ReHypeKatex,
          {
            output: 'mathml',
          },
        ],
      ]}
      components={{
        pre: ({ children, className, node }) => {
          return <div>{children}</div>;
        },
        a: ({ children, className, node }) => {
          return (
            <a {...node?.properties} target="_blank" rel="nofollow">
              {children}
            </a>
          );
        },
        table: ({ children }) => {
          return (
            <table className="border-collapse border border-gray-300 my-2 text-xs min-w-full whitespace-nowrap">
              {children}
            </table>
          );
        },
        thead: ({ children }) => {
          return (
            <thead className="!bg-[var(--theme-primary)] !text-[var(--theme-primary-text)]">
              {children}
            </thead>
          );
        },
        tbody: ({ children, className, node }) => {
          return <tbody>{children}</tbody>;
        },
        th: ({ children, className, node }) => {
          return (
            <th className="border border-gray-300 px-2 py-1 font-semibold text-left">
              {children}
            </th>
          );
        },
        td: ({ children, className, node }) => {
          return (
            <td className="border border-gray-300 px-2 py-1">{children}</td>
          );
        },
        br: ({ children, className, node }) => {
          return <br />;
        },
        code: ({ children, className, node }) => {
          const languageClasses = Array.isArray(node?.properties.className)
            ? node.properties.className
                .filter((e) => `${e}`.startsWith('language-'))
                .map((e) => `${e}`.replace('language-', ''))
            : [];
          const language =
            languageClasses.length > 0 ? languageClasses[0] : undefined;

          return (
            <CodeBlock
              className={className}
              isInline={
                node?.position?.start &&
                node?.position?.end &&
                node.position.start.line === node.position.end.line
              }
              language={language}
            >
              {children}
            </CodeBlock>
          );
        },
      }}
      className="text-sm [&_p]:last:mb-0"
    >
      {text.replace(/\\\((.+?)\\\)/g, '$ $1 $')}
    </ReactMarkdown>
  );
};

export default FormattedMessage;
